/**
 * ProgramSessionForm Component
 * Reusable form component for collecting Program and Session data
 * Program data is long-term, Session data is year-specific with expiry
 */

import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCurrentSessionYear, calculateSessionExpiry } from '@/utils/sessionExpiryHelpers';

export default function ProgramSessionForm({
  program: initialProgram = '',
  session: initialSession = '',
  sessionYear: initialSessionYear = null,
  onChange,
  className = ''
}) {
  const [program, setProgram] = useState(initialProgram);
  const [session, setSession] = useState(initialSession);
  const [sessionYear, setSessionYear] = useState(initialSessionYear || getCurrentSessionYear());
  
  // Store latest onChange callback in a ref to avoid infinite loops
  // Since parent components pass inline arrow functions, they create new references on each render
  const onChangeRef = useRef(onChange);
  
  // Update ref when onChange changes (this doesn't trigger re-renders)
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // Update state when props change
  useEffect(() => {
    setProgram(initialProgram);
    setSession(initialSession);
    setSessionYear(initialSessionYear || getCurrentSessionYear());
  }, [initialProgram, initialSession, initialSessionYear]);
  
  // Notify parent of changes (only when values change, not when onChange reference changes)
  useEffect(() => {
    if (onChangeRef.current) {
      const expiryDate = session && sessionYear ? calculateSessionExpiry(sessionYear) : null;
      onChangeRef.current({
        program: program || null,
        session: session || null,
        sessionYear: session ? sessionYear : null,
        sessionExpiresAt: expiryDate
      });
    }
  }, [program, session, sessionYear]);
  
  const handleProgramChange = (e) => {
    setProgram(e.target.value);
  };
  
  const handleSessionChange = (e) => {
    setSession(e.target.value);
    // If session is cleared, clear sessionYear
    if (!e.target.value) {
      setSessionYear(getCurrentSessionYear());
    }
  };
  
  const handleSessionYearChange = (e) => {
    const year = parseInt(e.target.value, 10);
    if (!isNaN(year) && year > 2000 && year < 2100) {
      setSessionYear(year);
    }
  };
  
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Program Field */}
      <div className="space-y-2">
        <Label htmlFor="program">
          Program (Optional)
        </Label>
        <Input
          id="program"
          type="text"
          value={program}
          onChange={handleProgramChange}
          placeholder="e.g., Camp Isidore Alterman"
          className="w-full"
        />
        <p className="text-xs text-gray-500">
          Program data is long-term and persists across years
        </p>
      </div>
      
      {/* Session Fields */}
      <div className="space-y-2">
        <Label htmlFor="session">
          Session (Optional)
        </Label>
        <Input
          id="session"
          type="text"
          value={session}
          onChange={handleSessionChange}
          placeholder="e.g., CIA Week 1"
          className="w-full"
        />
        {session && (
          <div className="space-y-2">
            <Label htmlFor="sessionYear" className="text-sm">
              Session Year
            </Label>
            <Input
              id="sessionYear"
              type="number"
              value={sessionYear}
              onChange={handleSessionYearChange}
              min="2000"
              max="2100"
              className="w-full"
            />
          </div>
        )}
        <p className="text-xs text-gray-500">
          Session data is year-specific and expires after the camp year ends
        </p>
      </div>
      
      {/* Helper Text */}
      <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded-md">
        <strong>Tip:</strong> Program data is long-term (e.g., "Camp Isidore Alterman"). 
        Session data is year-specific (e.g., "CIA Week 1") and expires after the camp year.
      </div>
    </div>
  );
}
